import { format } from "date-fns";
import { Decoder, array, iso8601, nullable, number, object, string } from "decoders";
import { SiteData, siteDataDecoder } from "./site";
import { map, mean, prop } from "ramda";

export interface DashboardData {
  samples: SampleRecord[];
  unit: string;
  referenceLimits: {
    humanHealth: number | null;
    aquaticLife: number | null;
    drinkingWater: number | null;
  };
  sites: SiteData[];
}

export interface SampleRecord {
  siteName: string;
  sampleDate: Date;
  measurementResult: number;
}

export const sampleRecordDecoder: Decoder<SampleRecord> = object({
  siteName: string,
  sampleDate: iso8601,
  measurementResult: number,
});

export const dashboardDataDecoder: Decoder<DashboardData> = object({
  samples: array(sampleRecordDecoder),
  unit: string,
  referenceLimits: object({
    humanHealth: nullable(number),
    aquaticLife: nullable(number),
    drinkingWater: nullable(number),
  }),
  sites: array(siteDataDecoder),
});

export function getSampleDate(sample: SampleRecord): string {
  return format(sample.sampleDate, "yyyy-MM-dd");
}

export function findSampleMeasurementByDate(siteName: string, date: string, data: SampleRecord[]) {
  for (const siteData of data) {
    if (siteData.siteName === siteName && getSampleDate(siteData) === date) {
      return siteData.measurementResult;
    }
  }

  return null;
}

export function getReferenceLimitsExceedingSamples(data: DashboardData): [SampleRecord, number][] {
  return Object.values(data.referenceLimits)
    .map((referenceLimit) => getReferenceLimitExceedingSamples(data, referenceLimit))
    .flat();
}

function getReferenceLimitExceedingSamples(data: DashboardData, referenceLimit: number | null) {
  return referenceLimit === null
    ? []
    : data.samples
        .filter((sample) => sample.measurementResult > referenceLimit)
        .map((sample) => [sample, referenceLimit] as [SampleRecord, number]);
}

export function hasReferenceLimits(data: DashboardData): boolean {
  return Object.values(data.referenceLimits).some((limit) => limit !== null);
}

export interface SiteStats {
  siteName: string;
  siteMean: number;
  siteMin: number;
  siteMax: number;
  samples: number;
}

export function getDashboardStats(data: DashboardData, sites: string[]): SiteStats[] {
  return sites.map((site) => {
    const siteSamples = getSiteSamples(site, data.samples);

    return { ...getStats(siteSamples), siteName: site };
  });
}

export function getStats(siteSamples: SampleRecord[]) {
  if (siteSamples.length === 0) return { siteMean: 0, siteMin: 0, siteMax: 0, samples: 0 };

  const results = map(prop("measurementResult"), siteSamples);

  const siteMean = mean(map(prop("measurementResult"), siteSamples));
  const siteMin = Math.min(...results);
  const siteMax = Math.max(...results);

  return { siteMean, siteMin, siteMax, samples: siteSamples.length };
}

export function getSiteSamples(siteName: string, samples: SampleRecord[]) {
  return samples.filter((sample) => sample.siteName === siteName);
}
